import { z } from "zod";

import {
  campaignContentSlotV1Schema,
  type CampaignContentSlotV1,
  type CampaignSocialCreativeBriefV1,
} from "./campaign.ts";
import {
  isSocialPostTypeSupportedByPlatform,
  socialPostPlatformSchema,
  socialPostTypeSchema,
  type SocialPostType,
} from "./social-post-scene.ts";

export const STANDALONE_ASSET_BATCH_STATUSES = [
  "draft",
  "scheduled",
  "generating",
  "generated",
  "active",
  "archived",
  "failed",
] as const;
export type StandaloneAssetBatchStatus = (typeof STANDALONE_ASSET_BATCH_STATUSES)[number];
export const standaloneAssetBatchStatusSchema = z.enum(STANDALONE_ASSET_BATCH_STATUSES);

export const STANDALONE_ASSET_BATCH_MAX_ITEMS = 10;

export const STANDALONE_ASSET_GENERATION_POST_TYPES = [
  "feed_portrait",
  "feed_square",
  "story",
  "carousel_portrait",
  "carousel_square",
  "tiktok_portrait",
  "tiktok_carousel_portrait",
] as const satisfies readonly SocialPostType[];
export type StandaloneAssetGenerationPostType =
  (typeof STANDALONE_ASSET_GENERATION_POST_TYPES)[number];
export const standaloneAssetGenerationPostTypeSchema = z.enum(
  STANDALONE_ASSET_GENERATION_POST_TYPES,
);

export const STANDALONE_ASSET_BRAND_PRESENCE_MODES = ["branded", "non_branded"] as const;
export type StandaloneAssetBrandPresence = (typeof STANDALONE_ASSET_BRAND_PRESENCE_MODES)[number];
export const standaloneAssetBrandPresenceSchema = z.enum(STANDALONE_ASSET_BRAND_PRESENCE_MODES);

export const STANDALONE_ASSET_BATCH_CREATION_SOURCES = ["manual", "onboarding_bootstrap"] as const;
export type StandaloneAssetBatchCreationSource =
  (typeof STANDALONE_ASSET_BATCH_CREATION_SOURCES)[number];
export const standaloneAssetBatchCreationSourceSchema = z.enum(
  STANDALONE_ASSET_BATCH_CREATION_SOURCES,
);

export const standaloneAssetBatchBriefV1Schema = z.object({
  version: z.literal(1),
  prompt: z.string().trim().min(1),
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).nullable().optional().default(null),
  audienceOverride: z.string().trim().min(1).max(500).nullable().optional().default(null),
});
export type StandaloneAssetBatchBriefV1 = z.infer<typeof standaloneAssetBatchBriefV1Schema>;

export const standaloneAssetScheduleItemV1Schema = z
  .object({
    scheduledFor: z.string().datetime(),
    platform: socialPostPlatformSchema.default("instagram"),
    postType: socialPostTypeSchema,
    brandPresence: standaloneAssetBrandPresenceSchema.default("branded"),
    slot: campaignContentSlotV1Schema.optional(),
  })
  .superRefine((value, ctx) => {
    if (!isSocialPostTypeSupportedByPlatform(value.platform, value.postType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported post type for platform.",
      });
    }
  });
export type StandaloneAssetScheduleItemV1 = z.infer<typeof standaloneAssetScheduleItemV1Schema>;
export type StandaloneAssetScheduleItemV1Input = Omit<
  z.input<typeof standaloneAssetScheduleItemV1Schema>,
  "slot"
> & {
  slot?: CampaignContentSlotV1;
};

export const standaloneAssetBatchCreateRequestV1Schema = z.object({
  version: z.literal(1),
  prompt: z.string().trim().min(1).max(4_000),
  audienceOverride: z.string().trim().min(1).max(500).nullable().optional(),
  items: z.array(standaloneAssetScheduleItemV1Schema).min(1).max(STANDALONE_ASSET_BATCH_MAX_ITEMS),
});
export type StandaloneAssetBatchCreateRequestV1 = z.infer<
  typeof standaloneAssetBatchCreateRequestV1Schema
>;

export const standaloneAssetBatchScheduleV1Schema = z.object({
  version: z.literal(1),
  items: z.array(standaloneAssetScheduleItemV1Schema).min(1).max(STANDALONE_ASSET_BATCH_MAX_ITEMS),
});
export type StandaloneAssetBatchScheduleV1 = z.infer<typeof standaloneAssetBatchScheduleV1Schema>;

const SENTENCE_END_RE = /[.!?].*$/u;
const WHITESPACE_RE = /\s+/gu;

const STANDALONE_ASSET_SLOT_TEMPLATES = [
  {
    objective: "brand_awareness",
    objectiveLabel: "Cold-open moment",
    conceptLead:
      "Open with a cold-open human moment that makes the batch theme impossible to ignore",
    socialCreativeBrief: {
      style: "social_entertainment",
      mechanic: "relatable_moment",
      tensionLead: "The audience wants the polished outcome but recognizes the messy first moment",
    },
    visualApproach:
      "Observational third-person scene with generic fictional people mid-action, a visible tension point, and one clean copy zone.",
  },
  {
    objective: "product_education",
    objectiveLabel: "Tiny problem solved",
    conceptLead: "Turn the batch direction into one tiny problem the audience recognizes instantly",
    socialCreativeBrief: {
      style: "utility_education",
      mechanic: "mistake",
      tensionLead: "The audience knows the problem is small but feels it derail the whole workflow",
    },
    visualApproach:
      "Hands-in-action scene where observed hands, props, or generic fictional people show the problem before the copy explains it as a third-person action.",
  },
  {
    objective: "brand_awareness",
    objectiveLabel: "Before-after proof",
    conceptLead: "Show the before-after contrast in one frame instead of stating the benefit",
    socialCreativeBrief: {
      style: "aspirational_lifestyle",
      mechanic: "before_after",
      tensionLead:
        "The audience wants the after state but still recognizes the before-state friction",
    },
    visualApproach:
      "Artifact flat-lay with a split-feeling arrangement of receipts, checklist cues, or physical proof showing what changed.",
  },
  {
    objective: "lead_generation",
    objectiveLabel: "Honest objection",
    conceptLead: "Lead with the objection people are quietly thinking and answer it visually",
    socialCreativeBrief: {
      style: "expert_authority",
      mechanic: "confession",
      tensionLead: "The audience has a private objection that normal marketing copy usually dodges",
    },
    visualApproach:
      "Over-the-shoulder documentary scene with a practical hesitation visible through body language, workspace context, or tangible proof.",
  },
  {
    objective: "brand_awareness",
    objectiveLabel: "Relatable ritual",
    conceptLead: "Create a social ritual moment where the offer naturally earns its place",
    socialCreativeBrief: {
      style: "community_led",
      mechanic: "relatable_moment",
      tensionLead:
        "The audience shares the same ritual but rarely sees the underlying friction named",
    },
    visualApproach:
      "Wide/environmental lifestyle scene with generic fictional people, small imperfections, emotional reaction, and concrete props.",
  },
  {
    objective: "product_education",
    objectiveLabel: "Odd detail hook",
    conceptLead: "Hook on one oddly specific detail that reveals the product truth",
    socialCreativeBrief: {
      style: "social_entertainment",
      mechanic: "things_people_say",
      tensionLead:
        "The audience says one thing in public while the odd detail reveals what is really happening",
    },
    visualApproach:
      "Detail close-up editorial composition where the odd detail is physically obvious and connected to a human action or decision.",
  },
  {
    objective: "retention",
    objectiveLabel: "Saveable rule",
    conceptLead: "Frame the batch direction as a simple rule people would save for later",
    socialCreativeBrief: {
      style: "utility_education",
      mechanic: "saveable_rule",
      tensionLead:
        "The audience wants a simple rule because the real-world choice feels needlessly ambiguous",
    },
    visualApproach:
      "Graphic/minimal reference composition with a physical card, calendar cue, or sparse object arrangement that makes the rule memorable.",
  },
  {
    objective: "lead_generation",
    objectiveLabel: "Receipts not claims",
    conceptLead:
      "Use receipts, artifacts, or observable behavior instead of abstract credibility claims",
    socialCreativeBrief: {
      style: "expert_authority",
      mechanic: "case_study",
      tensionLead:
        "The audience distrusts broad claims but responds to concrete proof they can inspect",
    },
    visualApproach:
      "Product/artifact-led editorial image with non-readable receipts, notes, packages, tools, or before-after proof that makes the claim feel earned.",
  },
  {
    objective: "product_education",
    objectiveLabel: "Contrarian tip",
    conceptLead:
      "Start with a useful contrarian tip that flips a common assumption in the batch direction",
    socialCreativeBrief: {
      style: "expert_authority",
      mechanic: "contrarian_take",
      tensionLead:
        "The audience follows the default advice even though the opposite move would work better",
    },
    visualApproach:
      "Observational comparison scene with one surprising visual contrast, not a generic checklist card.",
  },
  {
    objective: "sales_conversion",
    objectiveLabel: "Payoff scene",
    conceptLead: "Close on the payoff moment people want after the friction is gone",
    socialCreativeBrief: {
      style: "aspirational_lifestyle",
      mechanic: "before_after",
      tensionLead:
        "The audience wants the payoff but needs to believe the friction can actually disappear",
    },
    visualApproach:
      "Wide/environmental payoff scene where relief, momentum, or a completed real-world action is visible in the setting.",
  },
] satisfies Array<{
  objective: CampaignContentSlotV1["objective"];
  objectiveLabel: string;
  conceptLead: string;
  socialCreativeBrief: Omit<CampaignSocialCreativeBriefV1, "version" | "tension"> & {
    tensionLead: string;
  };
  visualApproach: string;
}>;

const toPromptTheme = (prompt: string): string => {
  const normalized = prompt.trim().replace(WHITESPACE_RE, " ").replace(SENTENCE_END_RE, "");
  return normalized || "the standalone asset brief";
};

export const deriveStandaloneAssetBatchName = (prompt: string): string => {
  const normalized = prompt.trim().replace(WHITESPACE_RE, " ").replace(SENTENCE_END_RE, "");
  if (!normalized) {
    return "Standalone assets";
  }

  const maxLength = 72;
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
};

export const createStandaloneAssetBatchBrief = (input: {
  prompt: string;
  name?: string | null;
  description?: string | null;
  audienceOverride?: string | null;
}): StandaloneAssetBatchBriefV1 =>
  standaloneAssetBatchBriefV1Schema.parse({
    version: 1,
    prompt: input.prompt,
    name: input.name?.trim() || deriveStandaloneAssetBatchName(input.prompt),
    description: input.description?.trim() || null,
    audienceOverride: input.audienceOverride?.trim() || null,
  });

export const toStandaloneAssetSchedule = (
  items: StandaloneAssetScheduleItemV1[],
): StandaloneAssetBatchScheduleV1 =>
  standaloneAssetBatchScheduleV1Schema.parse({
    version: 1,
    items,
  });

export const buildStandaloneAssetSlot = (input: {
  prompt: string;
  item: StandaloneAssetScheduleItemV1Input;
  index: number;
  audienceOverride?: string | null;
}): CampaignContentSlotV1 => {
  if (input.item.slot) {
    return input.item.slot;
  }

  const item = standaloneAssetScheduleItemV1Schema.parse(input.item);

  const template =
    STANDALONE_ASSET_SLOT_TEMPLATES[input.index % STANDALONE_ASSET_SLOT_TEMPLATES.length] ??
    STANDALONE_ASSET_SLOT_TEMPLATES[0]!;
  const theme = toPromptTheme(input.prompt);

  return campaignContentSlotV1Schema.parse({
    version: 1,
    concept: `${template.conceptLead}: ${theme}`,
    objective: template.objective,
    objectiveLabel: template.objectiveLabel,
    audienceSegment: input.audienceOverride?.trim() || null,
    callToAction: null,
    socialCreativeBrief: {
      version: 1,
      style: template.socialCreativeBrief.style,
      mechanic: template.socialCreativeBrief.mechanic,
      tension: `${template.socialCreativeBrief.tensionLead}: ${theme}`,
    },
    visualApproach: template.visualApproach,
    productionMode: "ai_generated_photo",
    requiredCapabilities: ["static_image_generation"],
    requiresSpecificPersonLikeness: false,
    requiresVideo: false,
    executionFeasibility: "supported",
    platform: item.platform,
    postType: item.postType,
  });
};

export const validateStandaloneAssetCreateRequest = (
  input: unknown,
): StandaloneAssetBatchCreateRequestV1 => standaloneAssetBatchCreateRequestV1Schema.parse(input);
